import economyCsv from "../tables/balance/Economy.csv?raw";
import masterCsv from "../tables/balance/MasterDefaults.csv?raw";
import unitCsv from "../tables/balance/UnitBalance.csv?raw";
import stockCsv from "../tables/balance/StockLoadout.csv?raw";
import eventCsv from "../tables/balance/EventSchedule.csv?raw";
import botCsv from "../tables/balance/BotPersonality.csv?raw";
import sessionCsv from "../tables/balance/ProtoSession.csv?raw";
import viewJson from "../tables/presentation/portal-ffa-view.json";
import { csvToKeyValue, csvToObjects, num, str } from "./parse-csv";

export type DropMode = "PreservedValue" | "LifetimeEarned";

export type SummonKind =
  | "guardian"
  | "soldier"
  | "raider"
  | "chaser"
  | "hero"
  | "weapon";

export type UnitRuntime = {
  role: SummonKind | "master";
  unitId: string;
  speedMul: number;
  maxHp: number;
  atk: number;
  atkInterval: number;
  atkRange: number;
  leash: number;
  summonCost: number;
  sigilCost: number;
  stock: number;
  manaEquivalent: number;
  radius: number;
  color: number;
  auraSpeedMul?: number;
  auraRadius?: number;
  knockback?: number;
};

type ViewTables = typeof viewJson;

function parseHexColor(raw: string): number {
  const cleaned = raw.replace(/^0x/i, "");
  return Number.parseInt(cleaned, 16);
}

function requireRow(
  rows: Record<string, string>[],
  unitId: string,
): Record<string, string> {
  const row = rows.find((r) => r.UnitId === unitId);
  if (!row) throw new Error(`[data] UnitBalance missing UnitId=${unitId}`);
  return row;
}

/**
 * Build runtime catalog from balance CSV + presentation JSON.
 * Strings are NOT embedded here — use `t()` from strings.ts.
 */
export function buildPortalFfaCatalog() {
  const economy = csvToKeyValue(economyCsv);
  const master = csvToKeyValue(masterCsv);
  const session = csvToKeyValue(sessionCsv);
  const units = csvToObjects(unitCsv);
  const stocks = csvToObjects(stockCsv);
  const events = csvToObjects(eventCsv);
  const bots = csvToObjects(botCsv);
  const view = viewJson as ViewTables;

  const stockMap: Record<string, number> = {};
  for (const s of stocks) stockMap[s.UnitId] = Number(s.Stock) || 0;

  const masterSpd = num(master, "SPD", 10);
  const masterSpeedPx = num(session, "MasterSpeedPx", 55);
  const unitScale = view.worldUnitToPx;

  const roleOf = view.unitIdByRole;

  const buildUnit = (role: SummonKind): UnitRuntime => {
    const unitId = roleOf[role];
    const row = requireRow(units, unitId);
    const spd = Number(row.SPD) || 0;
    const atkSpd = Number(row.ATKSPD) || 1;
    const radius = (view.radii as Record<string, number>)[role] ?? 8;
    const color = parseHexColor((view.colors as Record<string, string>)[role] ?? "0xffffff");
    return {
      role,
      unitId,
      speedMul: masterSpd > 0 ? spd / masterSpd : 1,
      maxHp: Number(row.MaxHP) || 1,
      atk: Number(row.ATK) || 0,
      atkInterval: atkSpd > 0 ? 1 / atkSpd : 1,
      atkRange: (Number(row.AtkRange) || 1) * unitScale,
      leash: (Number(row.LeashDist) || 0) * unitScale,
      summonCost: Number(row.SummonManaCost) || 0,
      sigilCost: Number(row.SigilCost) || 0,
      stock: stockMap[unitId] ?? 0,
      manaEquivalent: Number(row.ManaEquivalentCost) || 0,
      radius,
      color,
      ...(role === "hero"
        ? {
            auraSpeedMul: view.hero.auraSpeedMul,
            auraRadius: view.hero.auraRadiusPx,
          }
        : {}),
      ...(role === "weapon"
        ? {
            knockback: view.weapon.knockbackPx,
          }
        : {}),
    };
  };

  const pact = events.find((e) => e.EventId === "PortalPact");
  const warp = events.find((e) => e.EventId === "WarpCore");

  const botTable: Record<
    string,
    {
      manaSeek: number;
      attackWeak: number;
      opportunistLoot: number;
      fearStrong: number;
      leaderHunt: number;
      summonThreshold: number;
    }
  > = {};
  for (const b of bots) {
    botTable[b.PersonalityId] = {
      manaSeek: Number(b.ManaSeek) || 0,
      attackWeak: Number(b.AttackWeak) || 0,
      opportunistLoot: Number(b.OpportunistLoot) || 0,
      fearStrong: Number(b.FearStrong) || 0,
      leaderHunt: Number(b.LeaderHunt) || 0,
      summonThreshold: Number(b.SummonThreshold) || 0,
    };
  }

  const masterRow = requireRow(units, roleOf.master);
  const masterAtk = Number(masterRow.ATK) || 5;
  const masterAtkSpd = Number(masterRow.ATKSPD) || 0.9;
  const masterAtkRange =
    (Number(masterRow.AtkRange) || 2.5) * unitScale;

  const dropModeRaw = str(economy, "DropMode", "PreservedValue");
  const dropMode: DropMode =
    dropModeRaw === "LifetimeEarned" ? "LifetimeEarned" : "PreservedValue";

  return {
    meta: {
      id: "portal-master-ffa",
      version: "0.1.0",
    },
    map: {
      width: num(session, "MapWidth", 2700),
      height: num(session, "MapHeight", 2700),
      outerMargin: num(session, "OuterMargin", 180),
    },
    master: {
      maxHp: num(master, "MaxHP", 800),
      speed: masterSpeedPx,
      starterMana: num(master, "StarterMana", 55),
      starterSigils: num(master, "StarterSigils", 0),
      spawnProtectSec: num(master, "SpawnProtectSec", 3),
      radius: view.radii.master,
      colorPlayer: parseHexColor(view.colors.playerMaster),
      colorBot: parseHexColor(view.colors.botMaster),
      atk: masterAtk,
      atkInterval: masterAtkSpd > 0 ? 1 / masterAtkSpd : 1,
      atkRange: masterAtkRange,
    },
    units: {
      guardian: buildUnit("guardian"),
      soldier: buildUnit("soldier"),
      raider: buildUnit("raider"),
      chaser: buildUnit("chaser"),
      hero: buildUnit("hero"),
      weapon: buildUnit("weapon"),
    },
    economy: {
      dropMode,
      pvpDeathRate: num(economy, "PvpDeathRate", 0.5),
      envDeathRate: num(economy, "EnvDeathRate", 0.1),
      unitDeathReturnRate: num(economy, "UnitDeathReturnRate", 0.15),
      specialResidualRate: num(economy, "SpecialResidualRate", 0.4),
      dropCollectDelaySec: num(economy, "DropCollectDelaySec", 1),
      dropExpireSec: num(economy, "DropExpireSec", 25),
      maxDropCap: num(economy, "MaxDropCap", 2000),
      maxOrbValue: num(economy, "MaxOrbValue", 40),
      collectRadius: num(economy, "CollectRadius", 2.5) * unitScale,
      unitKillMana: num(economy, "UnitKillMana", 3),
      sigilEveryMana: num(economy, "SigilEveryMana", 50),
      summonIntervalSec: num(economy, "SummonIntervalSec", 0.12),
      naturalManaIntervalSec: num(economy, "NaturalManaIntervalSec", 3),
      naturalOrbValue: num(economy, "NaturalManaOrbValue", 6),
      naturalClusterMin: num(economy, "NaturalManaClusterMin", 3),
      naturalClusterMax: num(economy, "NaturalManaClusterMax", 7),
      maxUnitsPerMaster: num(economy, "MaxUnitsPerMaster", 25),
    },
    session: {
      botCount: num(economy, "BotCount", 5),
      starterArmy: {
        guardian: num(session, "StarterGuardian", 2),
        soldier: num(session, "StarterSoldier", 4),
        raider: num(session, "StarterRaider", 2),
        chaser: num(session, "StarterChaser", 1),
      },
      leaderPulseIntervalSec: num(economy, "LeaderPulseIntervalSec", 8),
      reenterDelaySec: num(session, "ReenterDelaySec", 1.5),
    },
    events: {
      portalPactIntervalSec: Number(pact?.IntervalSec) || 90,
      portalPactDurationSec: Number(pact?.DurationSec) || 45,
      portalPactRadius: (Number(pact?.Radius) || 18) * unitScale,
      allianceEndGraceSec: num(economy, "AllianceEndGraceSec", 2),
      warpCoreIntervalSec: Number(warp?.IntervalSec) || 120,
      warpCoreDurationSec: Number(warp?.DurationSec) || 40,
      warpCoreRadius: (Number(warp?.Radius) || 12) * unitScale,
    },
    crowd: {
      sepRadiusMul: Number(view.crowd?.sepRadiusMul) || 1.2,
      sepStrength: Number(view.crowd?.sepStrength) || 100,
      maxSepNeighbors: Number(view.crowd?.maxSepNeighbors) || 6,
      walkSpeedEps: Number(view.crowd?.walkSpeedEps) || 12,
      walkEnter: Number(view.crowd?.walkEnter) || 22,
      walkExit: Number(view.crowd?.walkExit) || 6,
      facingFlipEps: Number(view.crowd?.facingFlipEps) || 25,
      sepDamping: Number(view.crowd?.sepDamping) || 0.35,
      arriveStop: Number(view.crowd?.arriveStop) || 36,
      arriveExitMul: Number(
        (view.crowd as { arriveExitMul?: number } | undefined)?.arriveExitMul,
      ) || 1.75,
      sepMaxImpulse: Number(
        (view.crowd as { sepMaxImpulse?: number } | undefined)?.sepMaxImpulse,
      ) || 90,
      restSepMul: Number(view.crowd?.restSepMul) || 0.2,
      facingHoldSec: Number(view.crowd?.facingHoldSec) || 0.35,
      animHoldSec: Number(
        (view.crowd as { animHoldSec?: number } | undefined)?.animHoldSec,
      ) || 0.28,
      aimTurnRate: Number(
        (view.crowd as { aimTurnRate?: number } | undefined)?.aimTurnRate,
      ) || 5,
      aimEngageMul: Number(
        (view.crowd as { aimEngageMul?: number } | undefined)?.aimEngageMul,
      ) || 1.5,
      slotRadius: Number(view.crowd?.slotRadius) || 28,
      displayPosFollowHz: Number(
        (view.crowd as { displayPosFollowHz?: number } | undefined)
          ?.displayPosFollowHz,
      ) || 14,
      displayPosSnapPx: Number(
        (view.crowd as { displayPosSnapPx?: number } | undefined)
          ?.displayPosSnapPx,
      ) || 48,
      sepCellSize: Number(
        (view.crowd as { sepCellSize?: number } | undefined)?.sepCellSize,
      ) || 48,
    },
    combat: {
      threatMul: Number(
        (view as { combat?: { threatMul?: number } }).combat?.threatMul,
      ) || 1.35,
      acquireMul: Number(
        (view as { combat?: { acquireMul?: number } }).combat?.acquireMul,
      ) || 1,
      moveGraceSec: Number(
        (view as { combat?: { moveGraceSec?: number } }).combat?.moveGraceSec,
      ) || 1.25,
      maxMarchSec: Number(
        (view as { combat?: { maxMarchSec?: number } }).combat?.maxMarchSec,
      ) || 8,
      postArriveSleepSec: (() => {
        const v = Number(
          (view as { combat?: { postArriveSleepSec?: number } }).combat
            ?.postArriveSleepSec,
        );
        return Number.isFinite(v) ? v : 0.3;
      })(),
      marchInterruptMul: Number(
        (view as { combat?: { marchInterruptMul?: number } }).combat
          ?.marchInterruptMul,
      ) || 0.55,
      marchInterruptStandoffMul: Number(
        (view as { combat?: { marchInterruptStandoffMul?: number } }).combat
          ?.marchInterruptStandoffMul,
      ) || 0.22,
      marchInterruptCapPx: Number(
        (view as { combat?: { marchInterruptCapPx?: number } }).combat
          ?.marchInterruptCapPx,
      ) || 96,
      leashSlackPx: Number(
        (view as { combat?: { leashSlackPx?: number } }).combat?.leashSlackPx,
      ) || 80,
      engageAcquirePadPx: Number(
        (view as { combat?: { engageAcquirePadPx?: number } }).combat
          ?.engageAcquirePadPx,
      ) || 40,
      engageLeashAcquireMul: Number(
        (view as { combat?: { engageLeashAcquireMul?: number } }).combat
          ?.engageLeashAcquireMul,
      ) || 0.85,
      leashPullbackGoalBias: Number(
        (view as { combat?: { leashPullbackGoalBias?: number } }).combat
          ?.leashPullbackGoalBias,
      ) || 0.2,
      projectileRoles: Array.isArray(
        (view as { combat?: { projectileRoles?: string[] } }).combat
          ?.projectileRoles,
      )
        ? (view as { combat?: { projectileRoles?: string[] } }).combat!
            .projectileRoles!
        : ["chaser"],
    },
    fx: {
      hitFlashMs: Number(
        (view as { fx?: { hitFlashMs?: number } }).fx?.hitFlashMs,
      ) || 60,
      deathMs: Number((view as { fx?: { deathMs?: number } }).fx?.deathMs) || 280,
      masterDeathMs:
        Number((view as { fx?: { masterDeathMs?: number } }).fx?.masterDeathMs) ||
        420,
      floatDmgMs:
        Number((view as { fx?: { floatDmgMs?: number } }).fx?.floatDmgMs) || 500,
      projectileMs:
        Number((view as { fx?: { projectileMs?: number } }).fx?.projectileMs) ||
        160,
    },
    camera: {
      zoomMin: Number(view.camera?.zoomMin) || 0.35,
      zoomMax: Number(view.camera?.zoomMax) || 1,
      zoomDefault: Number(view.camera?.zoomDefault) || 0.55,
      zoomStep: Number(view.camera?.zoomStep) || 0.08,
      freeLookReturnSec: Number(
        (view.camera as { freeLookReturnSec?: number } | undefined)
          ?.freeLookReturnSec,
      ) || 1.5,
      refocusTweenSec: Number(
        (view.camera as { refocusTweenSec?: number } | undefined)
          ?.refocusTweenSec,
      ) || 0.45,
      hudInsetTopPx: Number(
        (view.camera as { hudInsetTopPx?: number } | undefined)?.hudInsetTopPx,
      ) || 170,
      hudInsetBottomPx: Number(
        (view.camera as { hudInsetBottomPx?: number } | undefined)
          ?.hudInsetBottomPx,
      ) || 150,
    },
    teamColors: Array.isArray(view.teamColors)
      ? (view.teamColors as string[]).map((c) => parseHexColor(c))
      : [0x3de0ff, 0xff3d7a, 0xff9f1c, 0xa3e635, 0xc084fc, 0xfb7185],
    mapLook: {
      base: parseHexColor(view.mapLook?.base ?? "0x152033"),
      playFill: parseHexColor(view.mapLook?.playFill ?? "0x1a2740"),
      grid: parseHexColor(view.mapLook?.grid ?? "0x2a3d5c"),
      patchA: parseHexColor(view.mapLook?.patchA ?? "0x1e3348"),
      patchB: parseHexColor(view.mapLook?.patchB ?? "0x243a52"),
      rim: parseHexColor(view.mapLook?.rim ?? "0x3a5080"),
      vignette: parseHexColor(view.mapLook?.vignette ?? "0x0a101c"),
      gridStep: Number(view.mapLook?.gridStep) || 96,
      patchCount: Number(view.mapLook?.patchCount) || 28,
      seed: Number(view.mapLook?.seed) || 42,
    },
    summon: {
      queueDepth: Number(view.summon?.queueDepth) || 3,
    },
    rig: view.rig,
    attackFx: (view as { attackFx?: unknown }).attackFx,
    hitProfiles: (view as { hitProfiles?: Record<string, unknown> }).hitProfiles ?? {
      master: { mode: "cone", maxTargets: 2, coneDeg: 70, secondaryMul: 0.75 },
      guardian: { mode: "cone", maxTargets: 2, coneDeg: 100, secondaryMul: 0.8 },
      soldier: { mode: "line", maxTargets: 2, coneDeg: 28, secondaryMul: 0.85 },
      raider: { mode: "cone", maxTargets: 3, coneDeg: 110, secondaryMul: 0.65 },
      chaser: {
        mode: "single",
        maxTargets: 1,
        coneDeg: 0,
        secondaryMul: 1,
        standoff: true,
        holdMul: 0.9,
        retreatMul: 0.45,
      },
      hero: { mode: "cone", maxTargets: 3, coneDeg: 100, secondaryMul: 0.7 },
      weapon: { mode: "circle", maxTargets: 12, coneDeg: 360, secondaryMul: 1 },
    },
    bots: botTable,
  } as const;
}

export type PortalFfaCatalog = ReturnType<typeof buildPortalFfaCatalog>;
